/**
 * PostosDominio — seção "Postos de Bipagem" da Config (brief §2).
 *
 * Banner de cobertura (GET /api/producao/config/cobertura-postos):
 *   - warning "N etapa(s) bipável(is) sem posto ativo: X, Y" — clicável,
 *     filtra a tabela para as etapas descobertas; some com cobertura 100%.
 *   - danger quando a etapa-Tambor está descoberta (gargalo sem ponto de
 *     registro = furo grave no DBR).
 * Tabela CRUD (table-layout fixed, zero scroll lateral) + form em
 * SlideOverPanel. Inativar é soft-delete (PATCH ativo=false — preserva o
 * histórico de bipagem). Adaptação vs brief: o posto aponta direto para a
 * ETAPA (modelo real do backend — EstacaoTrabalho é roadmap).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  CATALOGO_ETAPAS_INFO,
  atualizarPostoBipagem,
  criarPostoBipagem,
  listarPostosBipagem,
} from "../../../services/producaoV2";
import { unidadeDefault } from "../../../services/unidades";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  WarningMsg,
} from "../../Orcamentos/styles";
import { Mono, NotaDiscreta, TabelaFixa } from "../_shared/ui";
import {
  FieldControleCol,
  FieldLabelCol,
  FieldRowBox,
  InputConfig,
  SelectConfig,
  ToggleSwitch,
} from "./ui";

const ETAPAS_BIPAVEIS = CATALOGO_ETAPAS_INFO.filter((e) => e.bipavel);

function FormPosto({ aberto, posto, onFechar, onSalvo }) {
  const { request } = useAuth();
  const [nome, setNome] = useState("");
  const [etapaCodigo, setEtapaCodigo] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setNome(posto?.nome || "");
    setEtapaCodigo(posto?.etapa_codigo || "");
    setAtivo(posto ? posto.ativo : true);
    setErro(null);
  }, [aberto, posto]);

  const salvar = async () => {
    setErro(null);
    if (!nome.trim() || !etapaCodigo) {
      setErro("Informe o nome e a etapa do posto.");
      throw new Error("validação");
    }
    try {
      if (posto) {
        await atualizarPostoBipagem(request, posto.id, {
          nome: nome.trim(),
          etapa_codigo: etapaCodigo,
          ativo,
        });
      } else {
        const unidade = await unidadeDefault(request);
        if (!unidade?.id) {
          setErro("Nenhuma unidade cadastrada — cadastre a fábrica primeiro.");
          throw new Error("sem unidade");
        }
        await criarPostoBipagem(request, {
          nome: nome.trim(),
          etapa_codigo: etapaCodigo,
          unidade: unidade.id,
          ativo,
        });
      }
      onSalvo();
      onFechar();
    } catch (err) {
      if (!erro) setErro(err?.message || "Não foi possível salvar o posto.");
      throw err;
    }
  };

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title={posto ? `Editar posto ${posto.numero}` : "Criar posto de bipagem"}
      subtitle="Ponto físico de registro — aparece no terminal do chão de fábrica"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
          <SecondaryButton type="button" onClick={onFechar}>
            Cancelar
          </SecondaryButton>
          <AsyncButton
            silentError
            successMessage={posto ? "Posto atualizado." : "Posto criado."}
            onClick={salvar}
          >
            Salvar posto
          </AsyncButton>
        </div>
      }
    >
      <FieldRowBox>
        <FieldLabelCol>Nome *</FieldLabelCol>
        <FieldControleCol>
          <InputConfig
            type="text"
            value={nome}
            maxLength={120}
            style={{ maxWidth: "100%" }}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Bancada de pintura 1"
          />
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Etapa *</FieldLabelCol>
        <FieldControleCol>
          <SelectConfig
            value={etapaCodigo}
            onChange={(e) => setEtapaCodigo(e.target.value)}
          >
            <option value="">Selecione…</option>
            <optgroup label="F5 Industrialização">
              {ETAPAS_BIPAVEIS.filter((e) => e.fase === "INDUSTRIALIZACAO").map(
                (e) => (
                  <option key={e.codigo} value={e.codigo}>
                    {e.label}
                  </option>
                ),
              )}
            </optgroup>
            <optgroup label="F6 Marcenaria">
              {ETAPAS_BIPAVEIS.filter((e) => e.fase === "MARCENARIA").map(
                (e) => (
                  <option key={e.codigo} value={e.codigo}>
                    {e.label}
                  </option>
                ),
              )}
            </optgroup>
          </SelectConfig>
          <NotaDiscreta style={{ margin: 0 }}>
            A etapa que este posto registra na bipagem.
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Ativo</FieldLabelCol>
        <FieldControleCol>
          <ToggleSwitch
            checked={ativo}
            onChange={setAtivo}
            rotulo={ativo ? "Ativo" : "Inativo"}
          />
          <NotaDiscreta style={{ margin: 0 }}>
            Inativo some do terminal e do banner de cobertura; o histórico de
            bipagens é preservado.
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>
      {erro && <ErrorMsg style={{ marginTop: 12 }}>{erro}</ErrorMsg>}
    </SlideOverPanel>
  );
}

export default function PostosDominio({ cobertura, onCoberturaMudou }) {
  const { request, hasPermission } = useAuth();
  const podeGerir = hasPermission("posto_bipagem:manage");

  const [postos, setPostos] = useState(null);
  const [erro, setErro] = useState(null);
  const [filtroDescobertas, setFiltroDescobertas] = useState(false);
  const [form, setForm] = useState(null); // null | {posto?: obj}

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const resposta = await listarPostosBipagem(request, {});
      setPostos(Array.isArray(resposta) ? resposta : resposta?.results || []);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar os postos.");
      setPostos(null);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const descobertas = useMemo(
    () => cobertura?.descobertas || [],
    [cobertura],
  );
  const rotulosDescobertas = useMemo(() => {
    const porCodigo = Object.fromEntries(
      (cobertura?.etapas || []).map((e) => [e.codigo, e.label]),
    );
    return descobertas.map((c) => porCodigo[c] || c);
  }, [cobertura, descobertas]);

  const linhas = useMemo(() => {
    if (!Array.isArray(postos)) return [];
    if (!filtroDescobertas) return postos;
    const set = new Set(descobertas);
    return postos.filter((p) => set.has(p.etapa_codigo));
  }, [postos, filtroDescobertas, descobertas]);

  const aoMudar = () => {
    carregar();
    onCoberturaMudou?.();
  };

  return (
    <>
      {cobertura && descobertas.length > 0 && (
        <WarningMsg
          as="button"
          type="button"
          onClick={() => setFiltroDescobertas((v) => !v)}
          style={{
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
            fontFamily: "inherit",
            ...(cobertura.tambor_descoberto
              ? {
                  background: "var(--danger-bg)",
                  borderColor: "var(--danger-border)",
                  color: "var(--danger-text)",
                }
              : null),
          }}
          title={
            filtroDescobertas
              ? "Clique para ver todos os postos"
              : "Clique para filtrar as etapas descobertas"
          }
        >
          {descobertas.length === 1
            ? `1 etapa bipável sem posto ativo: ${rotulosDescobertas[0]}`
            : `${descobertas.length} etapas bipáveis sem posto ativo: ${rotulosDescobertas.join(", ")}`}
          {cobertura.tambor_descoberto && cobertura.tambor
            ? ` — a etapa-Tambor (${cobertura.tambor.label}) está descoberta: gargalo sem ponto de registro.`
            : ""}
          {filtroDescobertas ? " (filtro ativo)" : ""}
        </WarningMsg>
      )}

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <SectionTitle style={{ margin: 0 }}>Postos de Bipagem</SectionTitle>
          {podeGerir && (
            <PrimaryButton type="button" onClick={() => setForm({})}>
              + Criar posto
            </PrimaryButton>
          )}
        </div>

        {erro && <ErrorMsg style={{ marginTop: 10 }}>{erro}</ErrorMsg>}
        {postos === null && !erro && <EmptyMsg>Carregando…</EmptyMsg>}
        {Array.isArray(postos) && postos.length === 0 && (
          <EmptyMsg>
            Nenhum posto de bipagem cadastrado nesta fábrica.
          </EmptyMsg>
        )}

        {linhas.length > 0 && (
          <TabelaFixa style={{ marginTop: 10 }}>
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome</th>
                <th>Etapa</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((posto) => (
                <tr
                  key={posto.id}
                  data-clicavel={podeGerir ? "true" : undefined}
                  onClick={() => podeGerir && setForm({ posto })}
                  title={podeGerir ? "Editar posto" : posto.nome}
                >
                  <td>
                    <Mono>{posto.numero}</Mono>
                  </td>
                  <td title={posto.nome}>{posto.nome}</td>
                  <td>
                    <Pill $tone="neutral">{posto.etapa_label}</Pill>
                  </td>
                  <td>
                    {posto.ativo ? (
                      <Pill $tone="success">Ativo</Pill>
                    ) : (
                      <Pill $tone="neutral">Inativo</Pill>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {podeGerir ? (
                      <span style={{ display: "inline-flex", gap: 6 }}>
                        <SecondaryButton
                          type="button"
                          onClick={() => setForm({ posto })}
                        >
                          Editar
                        </SecondaryButton>
                        <AsyncButton
                          variant="secondary"
                          silentError={false}
                          successMessage={
                            posto.ativo ? "Posto inativado." : "Posto reativado."
                          }
                          onClick={async () => {
                            await atualizarPostoBipagem(request, posto.id, {
                              ativo: !posto.ativo,
                            });
                            aoMudar();
                          }}
                        >
                          {posto.ativo ? "Inativar" : "Reativar"}
                        </AsyncButton>
                      </span>
                    ) : (
                      "–"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaFixa>
        )}
        {filtroDescobertas && linhas.length === 0 && (postos || []).length > 0 && (
          <EmptyMsg>
            Nenhum posto (nem inativo) nas etapas descobertas — crie o
            primeiro posto para cobrir o registro.
          </EmptyMsg>
        )}
        <NotaDiscreta>
          Inativar é soft-delete: preserva o histórico de ManufacturingEvent.
          Fases de projeto (F1–F4) não têm posto — bipagem existe só em
          F5/F6.
        </NotaDiscreta>
      </Card>

      <FormPosto
        aberto={Boolean(form)}
        posto={form?.posto || null}
        onFechar={() => setForm(null)}
        onSalvo={aoMudar}
      />
    </>
  );
}
